import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import {
	FeeScheduleListItemJSON,
	FeeScheduleLoadIssue,
	feeSchedulesApi,
} from "work/fee-schedules/_api/fee-schedules-api";
import FeeSchedulesQuerySchema, {
	FeeSchedulesQueryJSON,
} from "work/fee-schedules/_api/queries/contracts/_schema/fee-schedules-query-schema";
import FeeSchedulesQuery from "work/fee-schedules/_api/queries/contracts/fee-schedules-query";
import FeeScheduleSchema from "work/fee-schedules/_schemas/fee-schedule-schema";

export const isFeeScheduleLoadIssue = (value: object): value is FeeScheduleLoadIssue => {
	return "issue" in value;
};

export type FeeSchedulesStoreState = {
	byId: Record<string, FeeScheduleListItemJSON | null | undefined>;
	byQuery: Record<string, PaginatedResponse<FeeScheduleListItemJSON | FeeScheduleLoadIssue>>;
	interfaceQueries: {
		entries: Record<string, FeeSchedulesQueryJSON>;
		lastSuccessfulResponses: Record<string, PaginatedResponse<FeeScheduleListItemJSON | FeeScheduleLoadIssue>>;
	};
};

const initialState: FeeSchedulesStoreState = {
	byId: {},
	byQuery: {},
	interfaceQueries: {
		entries: {},
		lastSuccessfulResponses: {},
	},
};

type FeeSchedulesStateRoot = RootState & { workFeeSchedules: FeeSchedulesStoreState };

const feeSchedulesSlice = createSlice({
	name: "workFeeSchedules",
	initialState,
	reducers: {
		updateFeeSchedulesInterfaceQuery: (
			state,
			action: PayloadAction<{ interfaceName: string; query: FeeSchedulesQuery }>
		) => {
			const { interfaceName, query } = action.payload;
			state.interfaceQueries.entries[interfaceName] = FeeSchedulesQuerySchema.serialize(query);
		},
	},
	extraReducers: (builder) => {
		builder.addMatcher(feeSchedulesApi.endpoints.getFeeSchedulesByQuery.matchFulfilled, (state, action) => {
			if (!action.meta.arg.originalArgs) return;
			const { query, interfaceName } = action.meta.arg.originalArgs;
			if (!query || !query.filterParameters) return;
			const queryKey = query.cacheKey;
			const responseJson = action.payload;
			const validatedFeeScheduleData: (FeeScheduleListItemJSON | FeeScheduleLoadIssue)[] = [];

			for (const data of responseJson.data) {
				const feeScheduleJSON: FeeScheduleListItemJSON = data;
				let id = Guid.EMPTY;

				try {
					id = Guid.fromJSON(feeScheduleJSON.workAgreementId) ?? id;
					if (FeeScheduleSchema.validateJSON(feeScheduleJSON)) {
						validatedFeeScheduleData.push(feeScheduleJSON);
					}
				} catch (error) {
					validatedFeeScheduleData.push({
						id,
						issue: "Error loading fee schedule.",
					});
					console.error("Error validating fee schedule JSON:", error);
				}
			}

			const paginatedResponse = PaginatedResponse.from<FeeScheduleListItemJSON | FeeScheduleLoadIssue>(
				validatedFeeScheduleData,
				responseJson.headers
			);

			state.byQuery[queryKey] = paginatedResponse;
			if (interfaceName) {
				state.interfaceQueries.lastSuccessfulResponses[interfaceName] = paginatedResponse;
			}

			for (const feeScheduleData of paginatedResponse.data) {
				if (isFeeScheduleLoadIssue(feeScheduleData)) continue;

				const workAgreementId = Guid.fromJSON(feeScheduleData.workAgreementId);
				if (!workAgreementId) {
					console.error("Fee schedule does not have a valid work agreement id", feeScheduleData);
					continue;
				}

				state.byId[workAgreementId.value] = feeScheduleData;
			}
		});
	},
});

export const { updateFeeSchedulesInterfaceQuery } = feeSchedulesSlice.actions;

const selectFeeSchedulesEntriesForQuery = (state: RootState) =>
	(state as FeeSchedulesStateRoot).workFeeSchedules.byQuery;

export const selectFeeSchedulesByQueryKey = createSelector(
	[selectFeeSchedulesEntriesForQuery, (_: RootState, queryKey: string) => queryKey],
	(
		entries: Record<string, PaginatedResponse<FeeScheduleListItemJSON | FeeScheduleLoadIssue>>,
		queryKey: string
	): PaginatedResponse<FeeScheduleListItemJSON | FeeScheduleLoadIssue> | undefined => {
		const pagedJson = entries[queryKey];
		if (!pagedJson) return;
		return pagedJson;
	}
);

export const getFeeSchedulesByQuery = (query?: FeeSchedulesQuery) => (state: RootState) =>
	query ? selectFeeSchedulesByQueryKey(state, query.cacheKey) : undefined;

const selectFeeSchedulesQueryByInterface = createSelector(
	[
		(state: FeeSchedulesStateRoot) => state.workFeeSchedules.interfaceQueries.entries,
		(_state: FeeSchedulesStateRoot, interfaceName: string) => interfaceName,
	],
	(queries, interfaceName): FeeSchedulesQuery | undefined => {
		const json = queries[interfaceName];
		if (!json) return;
		return FeeSchedulesQuerySchema.deserialize(json);
	}
);

export const getFeeSchedulesQueryByInterface = (interfaceName: string) => (state: RootState) =>
	selectFeeSchedulesQueryByInterface(state as FeeSchedulesStateRoot, interfaceName);

const selectLastSuccessfulFeeSchedulesByInterface = createSelector(
	[
		(state: FeeSchedulesStateRoot, interfaceName: string) =>
			state.workFeeSchedules.interfaceQueries.lastSuccessfulResponses[interfaceName],
		(_state: FeeSchedulesStateRoot, interfaceName: string) => interfaceName,
	],
	(
		paginatedEntry: PaginatedResponse<FeeScheduleListItemJSON | FeeScheduleLoadIssue> | undefined
	): PaginatedResponse<FeeScheduleListItemJSON | FeeScheduleLoadIssue> | undefined => {
		if (!paginatedEntry) return;
		return paginatedEntry;
	}
);

export const getFeeSchedulesLastSuccessfulResponseByInterface =
	(interfaceName: string) => (state: RootState) =>
		selectLastSuccessfulFeeSchedulesByInterface(state as FeeSchedulesStateRoot, interfaceName);

export default feeSchedulesSlice;
